import { createContext, useContext, useEffect, useState } from "react";

const CartContext = createContext(null);
const CART_KEY = "gr_cart_v3";

// Distinct line per product+variant. Key falls back to the variant label when
// no SKU exists yet, so e.g. KPV 5 mg and KPV 10 mg coexist in the cart.
export const itemKey = (productId, variant) =>
    `${productId}::${variant?.sku || variant?.label || "default"}`;

export function CartProvider({ children }) {
    const [items, setItems] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem(CART_KEY) || "[]");
        } catch {
            return [];
        }
    });

    useEffect(() => {
        localStorage.setItem(CART_KEY, JSON.stringify(items));
    }, [items]);

    const add = (material, variant) => {
        const key = itemKey(material.id, variant);
        setItems((prev) => {
            const existing = prev.find((i) => i.key === key);
            if (existing) {
                return prev.map((i) => (i.key === key ? { ...i, qty: i.qty + 1 } : i));
            }
            return [
                ...prev,
                {
                    key,
                    id: material.id,
                    name: material.name,
                    category: material.category,
                    variant_label: variant?.label || null,
                    sku: variant?.sku || null,
                    lot_batch: variant?.lot_batch || null,
                    price: variant?.price ?? null,
                    qty: 1,
                    is_placeholder: !!material.is_placeholder,
                },
            ];
        });
    };

    const remove = (key) => setItems((prev) => prev.filter((i) => i.key !== key));
    const setQty = (key, qty) =>
        setItems((prev) =>
            qty < 1 ? prev.filter((i) => i.key !== key) : prev.map((i) => (i.key === key ? { ...i, qty } : i))
        );
    const clear = () => setItems([]);
    const has = (productId, variant) => items.some((i) => i.key === itemKey(productId, variant));
    const count = items.reduce((sum, i) => sum + (i.qty || 1), 0);

    return (
        <CartContext.Provider value={{ items, count, add, remove, setQty, clear, has }}>
            {children}
        </CartContext.Provider>
    );
}

export function useCart() {
    return useContext(CartContext);
}
